import React from "react";
import DashboardLayout from "../components/DashboardLayout";
import {
  Network,
  Github,
  MessageCircle,
  Cpu,
  Gauge,
  Puzzle,
  Code2,
  Users,
  Rocket,
  MonitorSmartphone,
  Server,
  LucideProps,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { NewsletterCallToAction } from "@/components/PageCallToAction";

const FeatureCard = ({
  icon: Icon,
  title,
  description,
}: {
  icon: React.ForwardRefExoticComponent<
    Omit<LucideProps, "ref"> & React.RefAttributes<SVGSVGElement>
  >;
  title: string;
  description: string;
}) => (
  <Card className="bg-card/50 border-none">
    <CardContent className="pt-6">
      <div className="mb-4 inline-flex items-center justify-center p-2 bg-primary/10 text-primary rounded-full">
        <Icon size={24} />
      </div>
      <h3 className="text-xl font-semibold mb-2">{title}</h3>
      <p className="text-muted-foreground">{description}</p>
    </CardContent>
  </Card>
);

const StepCard = ({
  title,
  description,
  icon: Icon,
}: {
  icon: React.ForwardRefExoticComponent<
    Omit<LucideProps, "ref"> & React.RefAttributes<SVGSVGElement>
  >;
  title: string;
  description: string;
}) => (
  <Card className="border rounded-xl overflow-hidden">
    <CardContent className="p-6">
      <div className="flex flex-col space-y-4">
        <div className="flex items-center justify-between">
          <div className="p-2 w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
            <Icon className="w-6 h-6 text-primary" />
          </div>
        </div>
        <h3 className="text-xl font-semibold text-primary px-0">{title}</h3>
        <p className="text-muted-foreground">{description}</p>
      </div>
    </CardContent>
  </Card>
);

const OpenSourceRL = () => {
  return (
    <DashboardLayout>
      <div className="max-w-5xl mx-auto space-y-12">
        <section className="space-y-6 pt-6 pb-8 text-center">
          <div className="inline-flex items-center justify-center p-2 bg-primary/10 text-primary rounded-full mx-auto">
            <Network size={24} />
          </div>
          <h1 className="text-4xl font-bold tracking-tight font-mono mb-4">
            Open‑Source Reinforcement Learning for Agents
          </h1>
          <p className="text-xl text-muted-foreground mb-8 max-w-3xl mx-auto">
            Train, evaluate, and iterate on LLM agents in hours — not weeks —
            using Agent Reinforcement Trainer (ART), the community‑driven
            framework built for multi‑turn agentic workflows.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 w-full">
            <Button
              asChild
              size="lg"
              className="primary-button bg-primary text-white hover:bg-primary/90 w-full sm:w-auto"
            >
              <a
                href="https://github.com/OpenPipe/ART"
                target="_blank"
                rel="noopener noreferrer"
              >
                <Github className="mr-2" />
                Get Started on GitHub
              </a>
            </Button>
            <Button
              variant="outline"
              size="lg"
              asChild
              className="outline-button-hover hover:text-white w-full sm:w-auto"
            >
              <a
                href="https://discord.gg/EceeVdhpxD"
                target="_blank"
                rel="noopener noreferrer"
              >
                <MessageCircle className="mr-2" />
                Join the Discord
              </a>
            </Button>
          </div>
        </section>

        <section>
          <h2 className="text-3xl font-bold text-center mb-8">Why ART?</h2>
          <div className="grid md:grid-cols-3 gap-6">
            <FeatureCard
              icon={Cpu}
              title="Built for Real Agents"
              description="Native support for multi‑turn roll‑outs so your agent learns in realistic conversations."
            />
            <FeatureCard
              icon={Gauge}
              title="GPU‑Smart & Budget‑Friendly"
              description="Unsloth‑powered memory savings and idle‑free roll‑outs let you fine‑tune a 7B model on free Colab T4s. Runs using H100s often cost under $100."
            />
            <FeatureCard
              icon={Puzzle}
              title="Drop‑In Integration"
              description="OpenAI‑compatible chat endpoint slots straight into your existing code or frameworks like CrewAI or the Agents SDK."
            />
          </div>
        </section>

        <section className="relative">
          <div className="absolute inset-0 bg-gradient-to-b from-primary/5 to-transparent -z-10 rounded-xl" />
          <div className="relative p-6 rounded-xl">
            <h2 className="text-3xl font-bold text-center mb-8">
              How It Works
            </h2>
            <div className="grid md:grid-cols-2 gap-6">
              <StepCard
                icon={MonitorSmartphone}
                title="Frontend"
                description="Define your agent's roll‑out loop and reward function, keeping your local tooling while GPUs churn in the cloud."
              />
              <StepCard
                icon={Server}
                title="Backend"
                description="Send requests to ART's OpenAI‑style endpoint to run inference & GRPO training, with progressively better checkpoints you can test locally or A/B in production."
              />
            </div>
          </div>
        </section>

        <section>
          <h2 className="text-3xl font-bold text-center mb-8">
            Built in the Open
          </h2>
          <div className="grid md:grid-cols-3 gap-6">
            <FeatureCard
              icon={Code2}
              title="Open Source"
              description="MIT‑licensed core & reference backend for maximum flexibility and community contribution."
            />
            <FeatureCard
              icon={Rocket}
              title="Active Development"
              description="Actively developed roadmap including TypeScript bindings, hosted backends, and full fine‑tuning support."
            />
            <FeatureCard
              icon={Users}
              title="Community-Driven"
              description="Issues, PRs, benchmarks, and feature requests welcome—shape the spec with us."
            />
          </div>
        </section>

        <section className="text-center">
          <h2 className="text-3xl font-bold mb-8">Join the Community</h2>
          <div className="max-w-2xl mx-auto mb-8">
            <p className="text-xl text-muted-foreground">
              It's still very early for both ART and open‑source RL, and we
              invite everyone to help us shape the project's direction.
            </p>
          </div>
          <Button size="lg" className="mx-auto primary-button" asChild>
            <a
              href="https://github.com/OpenPipe/ART"
              target="_blank"
              rel="noopener noreferrer"
            >
              <Rocket className="mr-2" />
              Get Started with ART
            </a>
          </Button>
        </section>
        <div className="pt-12">
          <NewsletterCallToAction />
        </div>
      </div>
    </DashboardLayout>
  );
};

export default OpenSourceRL;
